import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { ModalBase } from '@components/Modal';
import ContactChip from '@pages/ContactsPage/ContactChip';
import Autocomplete from '@components/Autocomplete';
import { alertActions, contactsActions } from '@actions';
import { contactsService } from '@services';
import { useTranslation, useThrottleValue } from '@hooks';
import { handleApiError } from '@helpers/api';
import { ModalType } from '@constants/modal';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: 564,
    },
    '& .modal-base-content': {
        padding: 20,
        '& .MuiFormControl-root': {
            '& label': {
                textTransform: 'capitalize',
            },
        },
    },
});

const Content = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateAreas: '"select1" "select2"',
    gridTemplateColumns: '100%',
});

const StyledAutocomplete = styled(Autocomplete)({
    marginBottom: 0,
    // INFO: only for merge
    '& .MuiOutlinedInput-notchedOutline legend span': {
        paddingRight: '20px',
    },
    // INFO: only for merge
    '& .MuiPaper-root': {
        flexWrap: 'nowrap',
    },
});

const ContactsMergeModal = ({ onMergeCallback, open }) => {
    const { t } = useTranslation();

    const [searchQuery, setSearchQuery] = useState('');

    const { contact, data, searchedContacts, contactEditModal } = useSelector(state => state.contacts);
    const dispatch = useDispatch();

    const throttledSearch = useThrottleValue(searchQuery, 1000);

    useEffect(() => {
        if (throttledSearch.length === 0) {
            dispatch(contactsActions.resetSearchedContacts());
            return;
        }
        if (throttledSearch.length >= 2) {
            dispatch(contactsActions.getSearchedContacts({ searchQuery: throttledSearch }, 0, 10));
        }
    }, [throttledSearch]);

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        formState: { isDirty, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            primaryContactId: contact?.id,
            secondaryContactId: '',
        },
    });

    const onCloseModal = () => {
        dispatch(contactsActions.setContactMergeModal(false));
        dispatch(contactsActions.resetSearchedContacts());
    };

    const onSubmit = async () => {
        const values = getValues();
        let newContact;
        try {
            newContact = await contactsService.mergeContacts(values);
        } catch (error) {
            handleApiError(error, 'ContactsPage.mergeFail');
            return;
        }

        dispatch(alertActions.success(t('ContactsPage.mergeSuccess')));
        dispatch(contactsActions.setContact(newContact));
        if (onMergeCallback) {
            onMergeCallback(newContact, values.secondaryContactId);
        }
        onCloseModal();
        if (contactEditModal.open) {
            dispatch(contactsActions.setContactEditModal({ open: false, type: ModalType.Create }));
        }
    };

    const filteredSearchedContacts = useMemo(() => {
        return searchedContacts.filter(contact => contact.id !== getValues().primaryContactId);
    }, [searchedContacts]);

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={onCloseModal}
            isDirty={isDirty}
            title={t('ContactsPage.mergeContactsTitle')}
            subtitle={t('ContactsPage.mergeContactsSubtitle')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.mergeContacts')}
            primaryActionDisabled={isSubmitting || !isDirty}
        >
            <Content>
                <Controller
                    control={control}
                    name="primaryContactId"
                    render={({ field, ref }) => (
                        <div style={{ gridArea: 'select1' }}>
                            <StyledAutocomplete
                                {...field}
                                ref={ref}
                                label={t('ContactsPage.mergeContactPrimary')}
                                options={data}
                                value={[contact || {}]}
                                disabled
                                ValueComponent={ContactChip}
                                inputProps={{
                                    style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                }}
                            />
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="secondaryContactId"
                    render={({ field, ref }) => (
                        <div style={{ gridArea: 'select2' }}>
                            <StyledAutocomplete
                                {...field}
                                ref={ref}
                                value={null}
                                label={t('ContactsPage.mergeContactSecondary')}
                                options={filteredSearchedContacts}
                                ValueComponent={ContactChip}
                                OptionComponent={ContactChip}
                                inputProps={{
                                    value: searchQuery,
                                    onChange: e => setSearchQuery(e.target.value),
                                    style: { display: field?.value?.length > 0 ? 'none' : 'block' },
                                }}
                                onChangeAutoComplete={items => {
                                    setValue('secondaryContactId', items.length ? items[0].id : '', {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        </div>
                    )}
                />
            </Content>
        </Modal>
    );
};

export default ContactsMergeModal;
